import { useEffect, useRef } from "react";
import useAuth from "../../hooks/useAuth";
import { fieldTrackingService } from "../../services/fieldTracking.service";
import { shouldShareLiveLocation } from "../../utils/fieldTrackingPermissions";
import { displayUserName } from "../../utils/displayUserName";
import {
  isLocationValidated,
  markLocationPermissionGranted,
  storeMyLastLocation,
} from "../../constants/locationPermission";
import { isOfflineWorkMode } from "../../constants/workMode";

const MIN_POST_INTERVAL_MS = 15_000;

export default function FieldLocationTracker() {
  const { auth } = useAuth();
  const role = auth?.user?.role;
  const watchIdRef = useRef(null);
  const lastPostRef = useRef(0);
  const postingRef = useRef(false);
  const tokenRef = useRef(auth?.token);
  const userRef = useRef(auth?.user);
  tokenRef.current = auth?.token;
  userRef.current = auth?.user;
  const userId = auth?.user?._id || auth?.user?.id || "";

  useEffect(() => {
    if (!shouldShareLiveLocation(role) || !auth?.token) {
      return undefined;
    }
    if (!navigator.geolocation) {
      return undefined;
    }

    const postPosition = async (position) => {
      const token = tokenRef.current;
      if (!token || isOfflineWorkMode() || navigator.onLine === false) return;
      const now = Date.now();
      if (postingRef.current || now - lastPostRef.current < MIN_POST_INTERVAL_MS) {
        return;
      }
      postingRef.current = true;
      try {
        const { latitude, longitude, accuracy } = position.coords;
        const res = await fieldTrackingService.postMyLocation(
          { latitude, longitude, accuracy },
          token
        );
        lastPostRef.current = Date.now();
        markLocationPermissionGranted();
        const saved = res?.data?.lastKnownLocation || {
          latitude,
          longitude,
          accuracy,
          updatedAt: new Date().toISOString(),
        };
        const user = userRef.current;
        const payload = {
          latitude: saved.latitude,
          longitude: saved.longitude,
          accuracy: saved.accuracy,
          updatedAt: saved.updatedAt,
          tracerId: String(user?.id || user?._id || ""),
          name: displayUserName(user),
          role,
        };
        storeMyLastLocation(payload);
        window.dispatchEvent(new CustomEvent("repo:my-location", { detail: payload }));
      } catch {
        /* retry on next tick */
      } finally {
        postingRef.current = false;
      }
    };

    const startWatch = () => {
      if (watchIdRef.current != null) return;
      watchIdRef.current = navigator.geolocation.watchPosition(
        postPosition,
        () => {},
        {
          enableHighAccuracy: true,
          maximumAge: 30_000,
          timeout: 20_000,
        }
      );
    };

    const stopWatch = () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    const tryStart = () => {
      if (isLocationValidated()) {
        startWatch();
      }
    };

    tryStart();
    window.addEventListener("repo:location-granted", tryStart);

    return () => {
      window.removeEventListener("repo:location-granted", tryStart);
      stopWatch();
    };
  }, [auth?.token, userId, role]);

  return null;
}
